import {
  el,
  text,
  type BlockSignal,
  type BlockSpec,
  type IRNode,
  type Issue,
  type LinkValue,
} from '@lp/shared';
import { compact, heading, paragraph } from '../shared/helpers.js';
import { cardCls } from '../shared/patterns.js';

export interface FeatureCardProps extends Record<string, unknown> {
  icon: string;
  title: string;
  body: string;
  link: LinkValue;
}

/**
 * A curated Lucide subset.
 *
 * Lucide is already a production dependency on collctiv.com. Offering the whole set would be
 * a styling free-for-all by another name, so this is the closed list the picker shows.
 */
export const CARD_ICONS = [
  'gift',
  'users',
  'wallet',
  'link',
  'qr-code',
  'shield-check',
  'clock',
  'party-popper',
  'cake',
  'plane',
  'graduation-cap',
  'heart-handshake',
  'receipt',
  'bell',
  'check',
] as const;

export const FeatureCard: BlockSpec<FeatureCardProps> = {
  id: 'FeatureCard',
  label: 'Feature card',
  category: 'landing',
  description: 'One card inside a feature grid.',

  fields: {
    icon: { type: 'icon', label: 'Icon' },
    title: { type: 'text', label: 'Title (H3)', maxChars: 60, lint: 'heading' },
    body: { type: 'textarea', label: 'Body', maxChars: 220, lint: 'body' },
    link: { type: 'link', label: 'Link', internalOnly: true, help: 'Optional. Counts towards the internal-link target.' },
  },

  defaultProps: {
    icon: 'gift',
    title: 'No account needed',
    body: 'Everyone paying in just taps your link. No signup, no app, no excuses.',
    link: { href: '', label: '' },
  },

  toIR(props): IRNode {
    // The icon is rendered as a decorative chip. Its glyph is resolved by the editor and, on
    // export, inlined as SVG — so it carries no text and needs no alt.
    const chip = el(
      'Block',
      'div',
      ['lp-icon-chip'],
      [],
      { attr: { 'data-lp-icon': props.icon }, displayName: 'Icon' },
    );

    const link =
      props.link?.href && props.link.label
        ? el('Link', 'a', ['lp-link'], [text(props.link.label)], {
            attr: { href: props.link.href },
            displayName: 'Card link',
          })
        : null;

    return el(
      'Block',
      'div',
      cardCls(),
      compact([
        chip,
        heading(3, props.title, 'white'),
        paragraph('lp-body', props.body, 'white'),
        link,
      ]),
      { displayName: 'Feature card' },
    );
  },

  signals(props): BlockSignal[] {
    const out: BlockSignal[] = [
      { kind: 'heading', level: 3, text: props.title },
      { kind: 'text', fieldPath: 'title', value: props.title, scope: 'heading' },
      { kind: 'text', fieldPath: 'body', value: props.body, scope: 'body' },
    ];
    if (props.link?.href) {
      out.push({ kind: 'link', href: props.link.href, anchor: props.link.label });
    }
    return out;
  },

  validate(props): Issue[] {
    const issues: Issue[] = [];
    if (props.link?.href && !props.link.label?.trim()) {
      issues.push({
        id: 'card-link-no-label',
        severity: 'warn',
        message: 'This card links somewhere but has no anchor text.',
        location: { fieldPath: 'link' },
      });
    }
    if (!props.title.trim()) {
      issues.push({
        id: 'card-no-title',
        severity: 'warn',
        message: 'A card with no title leaves a gap in the heading outline.',
        location: { fieldPath: 'title' },
      });
    }
    return issues;
  },
};
